import React, { useEffect, useState } from 'react';
import { Image } from 'antd';
import styled from 'styled-components';
import {
    DeleteOutlined,
    FileExcelOutlined,
    FileImageOutlined,
    FileOutlined,
    FilePdfOutlined,
    FilePptOutlined,
    FileTextOutlined,
    FileWordOutlined,
    FileZipOutlined,
    LinkOutlined,
    PictureOutlined,
} from '@ant-design/icons';
import { PanelSectionLabel } from '@/pages/inside/tenant/style';
import { getAttachmentFileExtension } from '@hooks/projects/projectHelpers';

const PendingAttRoot = styled.div`
    position: relative;
    width: 100%;
    min-width: 0;

    .pending-att-remove {
        opacity: 0;
        transition: opacity 0.15s ease;
        pointer-events: none;
    }
    &:hover .pending-att-remove {
        opacity: 1;
        pointer-events: auto;
    }
`;

const DeleteBtnStyle = {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 24,
    height: 24,
    borderRadius: 6,
    border: 'none',
    background: 'rgba(0,0,0,0.55)',
    color: '#fff',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
};

function ExtensionLabel({ ext }) {
    const label = (ext || 'FILE').toUpperCase();
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 32,
                padding: '3px 8px',
                borderRadius: 6,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.05em',
                color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))',
                background: 'var(--ant-color-fill-secondary, rgba(0,0,0,0.06))',
                border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                fontFamily: 'var(--font-family-geist), ui-sans-serif, system-ui, sans-serif',
            }}
        >
            {label}
        </span>
    );
}

function extFromMime(mime) {
    const m = String(mime || '').toLowerCase();
    if (!m) return '';
    if (m === 'application/pdf') return 'PDF';
    if (m.includes('wordprocessingml') || m === 'application/msword') return 'DOC';
    if (m.includes('spreadsheetml') || m === 'application/vnd.ms-excel') return 'XLS';
    if (m.includes('presentationml') || m === 'application/vnd.ms-powerpoint') return 'PPT';
    if (m === 'text/plain') return 'TXT';
    if (m === 'application/zip') return 'ZIP';
    if (m.startsWith('image/')) {
        const sub = m.split('/')[1] || '';
        if (sub === 'jpeg') return 'JPG';
        return sub ? sub.toUpperCase().slice(0, 8) : 'IMG';
    }
    return '';
}

function resolveExtensionLabel(item) {
    const fromName = getAttachmentFileExtension(item?.name || '');
    if (fromName) return fromName;
    return extFromMime(item?.type);
}

function FileIconForExtension(extRaw) {
    const e = String(extRaw || '')
        .toLowerCase()
        .replace(/^\./, '');
    const size = 26;
    const common = { fontSize: size, flexShrink: 0 };
    if (e === 'pdf') return <FilePdfOutlined style={{ ...common, color: '#e11d48' }} />;
    if (e === 'doc' || e === 'docx') return <FileWordOutlined style={{ ...common, color: '#2563eb' }} />;
    if (e === 'xls' || e === 'xlsx' || e === 'csv') return <FileExcelOutlined style={{ ...common, color: '#16a34a' }} />;
    if (e === 'ppt' || e === 'pptx') return <FilePptOutlined style={{ ...common, color: '#ea580c' }} />;
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(e)) return <FileZipOutlined style={{ ...common, color: '#ca8a04' }} />;
    if (['txt', 'md', 'json', 'xml', 'log'].includes(e)) return <FileTextOutlined style={{ ...common, color: 'var(--ant-color-text-secondary)' }} />;
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(e))
        return <FileImageOutlined style={{ ...common, color: '#7c3aed' }} />;
    return <FileOutlined style={{ ...common, color: 'var(--ant-color-text-secondary)' }} />;
}

export const LinkRow = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    border-radius: 999px;
    border: 1px solid rgba(124, 58, 237, 0.2);
    background: var(--ant-color-primary-bg, rgba(124, 58, 237, 0.08));
    color: var(--ant-color-primary, #7c3aed);
    font-size: 13px;
    font-weight: 400;
    transition: padding 0.2s ease;
    padding: 5px 10px;
    overflow: hidden;

    a {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: var(--ant-color-primary, #7c3aed);
        text-decoration: none;
        word-break: break-all;
        white-space: nowrap;
        overflow: hidden;
        max-width: 100%;
        padding-bottom: 2px;
    }

    a span {
        position: relative;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        padding-bottom: 1px;

        &::after {
            content: '';
            position: absolute;
            width: 100%;
            height: 1.5px;
            bottom: 0;
            left: 0;
            background-color: var(--ant-color-primary, #7c3aed);
            transform: scaleX(0);
            transform-origin: bottom left;
            transition: transform 0.25s ease-out;
        }
    }

    a span:hover::after {
        transform: scaleX(1);
    }

    ${(props) =>
        props.$hasRemove &&
        `
        &:hover {
            padding-right: 28px;
        }
    `}

    .pending-link-remove {
        position: absolute;
        right: 6px;
        top: 50%;
        transform: translateY(-50%) scale(0.8);
        width: 18px;
        height: 18px;
        border-radius: 999px;
        border: none;
        background: rgba(0, 0, 0, 0.45);
        color: #fff;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        z-index: 2;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.2s ease, transform 0.2s ease, background-color 0.2s ease;
    }

    &:hover .pending-link-remove {
        opacity: 1;
        pointer-events: auto;
        transform: translateY(-50%) scale(1);
    }

    .pending-link-remove:hover {
        background-color: #ef4444;
    }
`;

/**
 * Pending file tile below rich text (Create sprint / Create task / QA submission).
 * Images: full-width preview, no card shell. Files: extension icon + extension label.
 * Delete appears on hover.
 */
export function PendingAttachmentTile({
    item,
    onRemove,
    truncateFileName,
    /** When false, hides optional full-name line under file row (submission modal). */
    showBottomFileName = true,
}) {
    const [imgBroken, setImgBroken] = useState(false);
    const isImageMime = (item?.type || '').startsWith('image/');
    const previewUrl = isImageMime ? item?.preview || '' : '';
    const extLabel = resolveExtensionLabel(item);

    useEffect(() => {
        setImgBroken(false);
    }, [previewUrl]);

    const removeBtn =
        typeof onRemove === 'function' ? (
            <button type="button" className="pending-att-remove" aria-label="Remove attachment" onClick={onRemove} style={DeleteBtnStyle}>
                <DeleteOutlined style={{ fontSize: 12 }} />
            </button>
        ) : null;

    if (isImageMime && previewUrl && !imgBroken) {
        return (
            <PendingAttRoot>
                {removeBtn}
                <Image
                    src={previewUrl}
                    alt=""
                    onError={() => setImgBroken(true)}
                    preview={{ rootClassName: 'global-image-preview-root' }}
                    style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: 120,
                        display: 'block',
                        objectFit: 'cover',
                        borderRadius: 8,
                        verticalAlign: 'middle',
                    }}
                />
                {showBottomFileName ? (
                    <span
                        title={item?.name}
                        style={{
                            display: 'block',
                            marginTop: 6,
                            fontSize: 11,
                            lineHeight: 1.3,
                            color: 'var(--ant-color-text-secondary)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {truncateFileName ? truncateFileName(item?.name || 'Image') : item?.name || 'Image'}
                    </span>
                ) : null}
            </PendingAttRoot>
        );
    }

    if (isImageMime && (!previewUrl || imgBroken)) {
        return (
            <PendingAttRoot>
                {removeBtn}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 72,
                        borderRadius: 8,
                        background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.04))',
                    }}
                >
                    <PictureOutlined style={{ fontSize: 28, color: 'var(--ant-color-text-tertiary)' }} />
                </div>
            </PendingAttRoot>
        );
    }

    return (
        <PendingAttRoot>
            {removeBtn}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '4px 0',
                    minWidth: 0,
                }}
            >
                {FileIconForExtension(extLabel)}
                <ExtensionLabel ext={extLabel || 'FILE'} />
            </div>
            {showBottomFileName ? (
                <span
                    title={item?.name}
                    style={{
                        fontSize: 11,
                        lineHeight: 1.3,
                        color: 'var(--ant-color-text-secondary)',
                        width: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        paddingLeft: 2,
                    }}
                >
                    {truncateFileName ? truncateFileName(item?.name || 'attachment') : item?.name || 'attachment'}
                </span>
            ) : null}
        </PendingAttRoot>
    );
}

/** Pending link chips below rich text — delete on chip hover. */
export function PendingLinkWrap({ links, onRemoveLink }) {
    if (!links?.length) return null;
    const hasRemove = typeof onRemoveLink === 'function';
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {links.map(({ id, url, title }) => (
                <LinkRow key={id} $hasRemove={hasRemove}>
                    <a href={url} target="_blank" rel="noopener noreferrer">
                        <LinkOutlined style={{ fontSize: 12, flexShrink: 0 }} />
                        <span>{title || url}</span>
                    </a>
                    {hasRemove ? (
                        <button
                            type="button"
                            className="pending-link-remove"
                            aria-label="Remove link"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onRemoveLink(id);
                            }}
                            onPointerDown={(e) => e.stopPropagation()}
                        >
                            <DeleteOutlined style={{ fontSize: 11 }} />
                        </button>
                    ) : null}
                </LinkRow>
            ))}
        </div>
    );
}
